import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "app-header" }
const _hoisted_2 = { class: "app-header__bar" }
const _hoisted_3 = { class: "app-header__logo" }
const _hoisted_4 = ["src"]
const _hoisted_5 = {
  class: "app-header__nav",
  "aria-label": "Main navigation"
}
const _hoisted_6 = ["data-text"]
const _hoisted_7 = { class: "app-header__right" }
const _hoisted_8 = { class: "app-header__actions" }
const _hoisted_9 = { class: "app-header__notify" }
const _hoisted_10 = {
  key: 0,
  class: "app-header__notify-dot"
}
const _hoisted_11 = { class: "app-header__user-menu" }
const _hoisted_12 = ["src"]
const _hoisted_13 = {
  key: 1,
  class: "app-header__user-avatar"
}
const _hoisted_14 = { class: "app-header__user-info" }
const _hoisted_15 = ["title"]
const _hoisted_16 = ["title"]
const _hoisted_17 = {
  class: "user-menu",
  role: "menu",
  "aria-label": "User actions"
}
const _hoisted_18 = ["onClick"]
const _hoisted_19 = {
  key: 0,
  class: "user-menu__divider",
  "aria-hidden": "true"
}

import {
    computed,
    ref,
    type Component,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import Popover from 'primevue/popover';
import defaultLogo from '@/assets/logo.png';
import IconDashboard from '@/components/icons/IconDashboard.vue';
import IconPayments from '@/components/icons/IconPayments.vue';
import IconAccounts from '@/components/icons/IconAccounts.vue';
import IconNegotiations from '@/components/icons/IconNegotiations.vue';
import IconReports from '@/components/icons/IconReports.vue';
import IconUserMenuAccount from '@/components/icons/IconUserMenuAccount.vue';
import IconUserMenuLogout from '@/components/icons/IconUserMenuLogout.vue';
import NotificationDrawer from '@/components/notifications/NotificationDrawer.vue';
import HelpSupportModal from '@/components/modals/HelpSupportModal.vue';
import { useAuthStore } from '@/stores/auth';
import { useAccountStore } from '@/stores/account';
import { useSettingsStore } from '@/stores/settings';
import { useNotificationsStore } from '@/stores/notifications';
import { useProductTourStore } from '@/stores/productTour';
import { usePermissions } from '@/composables/usePermissions';
import type { ModuleKey } from '@/permissions';

interface NavItem {
    label: string
    icon: Component
    path: string
    module: ModuleKey
    exactMatch?: boolean
}

interface UserMenuAction {
    id: 'account' | 'logout'
    label: string
    icon: Component
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Header',
  setup(__props) {

const authStore = useAuthStore();
const accountStore = useAccountStore();
const settingsStore = useSettingsStore();
const notificationsStore = useNotificationsStore();
const productTour = useProductTourStore();
const { user } = storeToRefs(authStore);
const { hasUnread } = storeToRefs(notificationsStore);
const { openDrawer } = notificationsStore;
const route = useRoute();
const router = useRouter();
const userName = computed(() => user.value?.name || '');
const userCompany = computed(() => settingsStore.companyName || '');
const userInitials = computed(() => {
    const parts = userName.value.trim().split(/\s+/);
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
});

const userMenu = ref();
const userMenuActions: UserMenuAction[] = [
    { id: 'account', label: 'My Account', icon: IconUserMenuAccount },
    { id: 'logout', label: 'Logout', icon: IconUserMenuLogout },
];

const isUserMenuOpen = ref(false);
const isHelpModalVisible = ref(false);

function replayProductTour() {
    isHelpModalVisible.value = false;
    productTour.requestReplay();
}

const createHeaderTooltip = (label: string) => ({
    value: label,
    class: 'app-header-tooltip',
    showDelay: 80,
});

const notificationsTooltip = createHeaderTooltip('Notifications');
const settingsTooltip = createHeaderTooltip('Settings');
const helpTooltip = createHeaderTooltip('Help & Support');

const handleUserMenuAction = async (actionId: 'account' | 'logout') => {
    if (actionId === 'account') {
        router.push({ name: 'my-account' });
    } else if (actionId === 'logout') {
        await authStore.logoutUser();
    }

    userMenu.value?.hide();
};

const toggleUserMenu = (event: MouseEvent) => {
    userMenu.value?.toggle(event);
};

const navItems: NavItem[] = [
    {
        label: 'Dashboard',
        icon: IconDashboard,
        path: '/',
        module: 'dashboard',
        exactMatch: true,
    },
    { label: 'Payments', icon: IconPayments, path: '/payments', module: 'payments' },
    // PHASE2
    // { label: 'Accounts', icon: IconAccounts, path: '/accounts', module: 'accounts' },
    // { label: 'Negotiations', icon: IconNegotiations, path: '/negotiations', module: 'negotiations' },
    { label: 'Reports', icon: IconReports, path: '/reports', module: 'reports' },
];

const { can } = usePermissions();
const visibleNavItems = computed(() => navItems.filter((item) => can(item.module)));

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("header", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("img", {
          src: _unref(defaultLogo),
          alt: "Logo",
          class: "app-header__logo-img",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$router.push('/')))
        }, null, 8, _hoisted_4)
      ]),
      _createElementVNode("nav", _hoisted_5, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleNavItems.value, (item) => {
          return (_openBlock(), _createBlock(_component_RouterLink, {
            key: item.path,
            to: item.path,
            "data-product-tour": `nav-${item.module}`,
            class: _normalizeClass(["app-header__nav-item", {
                        'app-header__nav-item--active': item.exactMatch
                            ? _unref(route).path === item.path
                            : _unref(route).path === item.path || _unref(route).path.startsWith(item.path + '/'),
                    }]),
            "active-class": "",
            "exact-active-class": ""
          }, {
            default: _withCtx(() => [
              (_openBlock(), _createBlock(_resolveDynamicComponent(item.icon), {
                class: "app-header__nav-icon",
                "aria-hidden": "true"
              })),
              _createElementVNode("span", {
                "data-text": item.label
              }, _toDisplayString(item.label), 9, _hoisted_6)
            ]),
            _: 2
          }, 1032, ["to", "data-product-tour", "class"]))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          (_unref(can)('notifications'))
            ? _withDirectives((_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "app-header__action-btn",
                "data-product-tour": "notifications",
                "aria-label": "Notifications",
                onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(openDrawer) && _unref(openDrawer)(...args)))
              }, [
                _createElementVNode("span", _hoisted_9, [
                  _cache[7] || (_cache[7] = _createElementVNode("i", {
                    class: "pi pi-bell",
                    "aria-hidden": "true"
                  }, null, -1)),
                  (_unref(hasUnread))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_10))
                    : _createCommentVNode("", true)
                ])
              ])), [
                [
                  _directive_tooltip,
                  _unref(notificationsTooltip),
                  void 0,
                  { bottom: true }
                ]
              ])
            : _createCommentVNode("", true),
          (_unref(can)('settings'))
            ? _withDirectives((_openBlock(), _createElementBlock("button", {
                key: 1,
                class: "app-header__action-btn",
                "data-product-tour": "settings",
                "aria-label": "Settings",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(router).push({ name: 'settings' })))
              }, [...(_cache[8] || (_cache[8] = [
                _createElementVNode("i", {
                  class: "pi pi-cog",
                  "aria-hidden": "true"
                }, null, -1)
              ]))])), [
                [
                  _directive_tooltip,
                  _unref(settingsTooltip),
                  void 0,
                  { bottom: true }
                ]
              ])
            : _createCommentVNode("", true),
          _withDirectives((_openBlock(), _createElementBlock("button", {
            class: "app-header__action-btn",
            "data-product-tour": "help",
            "aria-label": "Help",
            onClick: _cache[3] || (_cache[3] = ($event: any) => (isHelpModalVisible.value = true))
          }, [...(_cache[9] || (_cache[9] = [
            _createElementVNode("i", {
              class: "pi pi-question-circle",
              "aria-hidden": "true"
            }, null, -1)
          ]))])), [
            [
              _directive_tooltip,
              _unref(helpTooltip),
              void 0,
              { bottom: true }
            ]
          ])
        ]),
        _cache[11] || (_cache[11] = _createElementVNode("span", {
          class: "app-header__divider",
          "aria-hidden": "true"
        }, null, -1)),
        _createElementVNode("div", _hoisted_11, [
          _createElementVNode("button", {
            class: _normalizeClass(["app-header__user", { 'app-header__user--open': isUserMenuOpen.value }]),
            "data-product-tour": "account",
            type: "button",
            "aria-label": "User menu",
            "aria-haspopup": "menu",
            onClick: toggleUserMenu
          }, [
            (_unref(accountStore).displayAvatarUrl)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  src: _unref(accountStore).displayAvatarUrl,
                  alt: "User avatar",
                  class: "app-header__user-avatar app-header__user-avatar--img"
                }, null, 8, _hoisted_12))
              : (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(userInitials.value), 1)),
            _createElementVNode("span", _hoisted_14, [
              _createElementVNode("span", { title: userName.value }, _toDisplayString(userName.value), 9, _hoisted_15),
              _createElementVNode("span", { title: userCompany.value }, _toDisplayString(userCompany.value), 9, _hoisted_16)
            ]),
            _cache[10] || (_cache[10] = _createElementVNode("i", {
              class: "pi pi-angle-down",
              "aria-hidden": "true"
            }, null, -1))
          ], 2),
          _createVNode(_unref(Popover), {
            ref_key: "userMenu",
            ref: userMenu,
            onShow: _cache[4] || (_cache[4] = ($event: any) => (isUserMenuOpen.value = true)),
            onHide: _cache[5] || (_cache[5] = ($event: any) => (isUserMenuOpen.value = false)),
            pt: {
                            root: { class: 'user-menu-popover' },
                            content: { class: 'user-menu-popover__content' },
                        }
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_17, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(userMenuActions, (action, index) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: action.id
                  }, [
                    _createElementVNode("button", {
                      class: "user-menu__action",
                      type: "button",
                      role: "menuitem",
                      onClick: ($event: any) => (handleUserMenuAction(action.id))
                    }, [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(action.icon), {
                        class: "user-menu__action-icon",
                        "aria-hidden": "true"
                      })),
                      _createElementVNode("span", null, _toDisplayString(action.label), 1)
                    ], 8, _hoisted_18),
                    (index < userMenuActions.length - 1)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_19))
                      : _createCommentVNode("", true)
                  ], 64))
                }), 64))
              ])
            ]),
            _: 1
          }, 512)
        ])
      ])
    ]),
    _createVNode(NotificationDrawer),
    _createVNode(HelpSupportModal, {
      visible: isHelpModalVisible.value,
      "onUpdate:visible": _cache[6] || (_cache[6] = ($event: any) => ((isHelpModalVisible).value = $event)),
      "can-replay-tour": _unref(productTour).eligible,
      onReplayTour: replayProductTour
    }, null, 8, ["visible", "can-replay-tour"])
  ]))
}
}

})